// src/store/middleware/socketMiddleware.ts - TRULY LAZY VERSION
import { Middleware } from 'redux';
import { socket, joinRoom, disconnectRoom, onMessage } from '../../hooks/socketService';
import { Logger } from '../../utils/logger';
import { 
  socketConnected, 
  socketDisconnected, 
  socketError, 
  joinRoomRequest, 
  joinRoomSuccess, 
  joinRoomFailure, 
  leaveRoomRequest, 
  leaveRoomSuccess, 
  leaveRoomFailure 
} from '../slices/socketSlice';
import { addMessage, Message } from '../slices/messagesSlice';
import { updateLastMessage } from '../slices/conversationsSlice';

// Interface for socket message type
interface SocketMessageData {
  createdAt: string;
  message: {
    from: string;
    id: string;
    timestamp: string;
    text: { 
      body: string 
    };
    type: string;
  };
  username: string;
  conversationId?: string;
  _id?: string;
  sender?: any;
  messageType?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'reaction';
  messageDelivery?: 'sent' | 'delivered' | 'read';
}

// ✅ Flag to track if socket listeners are initialized
let socketListenersInitialized = false;

// ✅ Function to initialize socket listeners ONLY when needed
const initializeSocketListeners = (store: any) => {
  if (socketListenersInitialized) {
    return; // Already initialized
  }
  
  Logger.log('🔌 LAZY: Initializing socket listeners for the first time');
  
  socket.on('connect', () => {
    Logger.log('Socket connected');
    store.dispatch(socketConnected());
  });

  socket.on('disconnect', (reason: any) => {
    Logger.log('Socket disconnected:', reason);
    store.dispatch(socketDisconnected());
  });

  socket.on('connect_error', (error: Error) => {
    const errorMessage = error.message || 'Connection error';
    Logger.log('Socket connection error:', errorMessage);
    store.dispatch(socketError(errorMessage));
  });

  socket.on('error', (error: unknown) => {
    const errorMessage = typeof error === 'string' ? error : 'Socket error';
    Logger.log('Socket error:', errorMessage);
    store.dispatch(socketError(errorMessage));
  });

  // Set up message handler
  onMessage((socketData: SocketMessageData) => {
    Logger.log('Message received:', socketData);
    
    const messageData: Message = {
      _id: socketData._id || `temp-${Date.now()}`,
      sender: socketData.sender || null,
      message: {
        body: socketData.message.text.body || '',
        id: socketData.message.id || undefined,
      },
      createdAt: socketData.createdAt || new Date().toISOString(),
      messageType: socketData.messageType || 'text',
      messageDelivery: socketData.messageDelivery || 'delivered',
    };
    
    store.dispatch(addMessage(messageData));
    
    if (socketData.conversationId) {
      store.dispatch(updateLastMessage({
        conversationId: socketData.conversationId,
        message: messageData
      }));
    }
  });

  socketListenersInitialized = true;
  Logger.log('🔌 LAZY: Socket listeners initialized successfully');
};

// Define the socket middleware
const socketMiddleware: Middleware = store => {
  // ✅ DON'T set up listeners immediately - wait for actual socket actions
  
  // The main middleware function
  return next => action => {
    // ✅ ONLY initialize socket when actually joining a room
    if (joinRoomRequest.match(action)) {
      const { username, room } = action.payload;
      Logger.log('🔌 LAZY: First-time socket usage - joining room:', room);
      
      // ✅ Initialize listeners NOW (when actually needed)
      initializeSocketListeners(store);
      
      // Join the room
      joinRoom(username, room);
      
      return next(action);
    }
    
    if (leaveRoomRequest.match(action)) {
      Logger.log('🔌 LAZY: Leaving room');
      
      // Only disconnect if listeners are initialized (socket is being used)
      if (socketListenersInitialized) {
        disconnectRoom();
      }
      
      return next(action);
    }
    
    // For all other actions, just pass them through
    return next(action);
  };
};

export default socketMiddleware;